import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowDownRight, Box } from "lucide-react";
import { Link } from "react-router-dom";
import { EASE, Eyebrow } from "./Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1663811396551-e639caee6e62?auto=format&fit=crop&w=1800&q=80";
const LINES = ["Crafted in", "timber, stone", "& quiet brass."];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.2, ease: EASE, delay: 0.25 + i * 0.14 }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ itemCount = 0, catCount = 0 }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 80, damping: 18 });
  const spotX = useTransform(mx, (v) => `${v * 100}%`);
  const spotY = useTransform(my, (v) => `${v * 100}%`);
  const spotlight = useMotionTemplate`radial-gradient(600px circle at ${spotX} ${spotY}, rgba(212,175,55,0.13), transparent 60%)`;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 pb-16 overflow-hidden">
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: spotlight }} />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center min-h-[calc(100svh-11rem)]">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}>
            <Eyebrow>Sterling Joinery — The Atlas · Vol. {new Date().getFullYear()}</Eyebrow>
          </motion.div>
          <h1 className="mt-8 font-display font-light text-[#F5F3EF] leading-[0.92] tracking-tight text-[3.4rem] sm:text-7xl lg:text-[7.4rem]">
            {LINES.map((l, i) => (
              <MaskLine key={l} i={i}>{i === 2 ? <em className="text-[#E5C158] not-italic font-display italic">{l}</em> : l}</MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }}
            className="mt-10 max-w-md text-base sm:text-lg text-[#A09E97] leading-relaxed">
            Every project we build, every handle, slab, board and appliance we specify — gathered into one living catalogue.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
            className="mt-12 flex flex-wrap items-center gap-8">
            <a href="#catalogue" data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[#D4AF37] text-[#0D0D11] pl-7 pr-2 py-2 text-sm font-medium hover:bg-[#E5C158] transition-colors duration-300">
              Explore the catalogue
              <span className="w-10 h-10 rounded-full bg-[#0D0D11] text-[#D4AF37] grid place-items-center group-hover:rotate-45 transition-transform duration-500">
                <ArrowDownRight className="w-4 h-4" />
              </span>
            </a>
            <Link to="/pairing" data-testid="hero-studio-btn"
              className="group inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/50 text-[#E5C158] pl-7 pr-2 py-2 text-sm font-medium hover:border-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors duration-300">
              Design your kitchen in 3D
              <span className="w-10 h-10 rounded-full bg-[#D4AF37] text-[#0D0D11] grid place-items-center group-hover:rotate-12 transition-transform duration-500">
                <Box className="w-4 h-4" />
              </span>
            </Link>
            <div className="flex gap-8 font-mono-j text-xs uppercase tracking-[0.2em] text-[#A09E97]">
              <span data-testid="hero-item-count"><b className="block font-display text-3xl normal-case tracking-normal text-[#F5F3EF] font-light">{itemCount}</b>Pieces</span>
              <span data-testid="hero-cat-count"><b className="block font-display text-3xl normal-case tracking-normal text-[#F5F3EF] font-light">{catCount}</b>Collections</span>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
            className="relative aspect-[4/5] w-full max-w-[520px] mx-auto rounded-t-[999px] overflow-hidden border border-[#D4AF37]/25">
            <motion.img src={HERO_IMG} alt="Dark timber kitchen joinery" style={{ y: imgY, scale: 1.2 }}
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D11]/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end" style={{ transform: "translateZ(40px)" }}>
              <span className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#E5C158]">Featured · PRJ-001</span>
              <span className="font-display italic text-2xl text-[#F5F3EF]">Obsidian</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, duration: 1 }}
            className="hidden sm:grid absolute -left-6 lg:-left-12 top-16 w-28 h-28 rounded-full place-items-center border border-[#D4AF37]/40 bg-[#0D0D11]/70 backdrop-blur-md">
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_24s_linear_infinite]">
              <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text fill="#C5A059" fontSize="9" letterSpacing="3" fontFamily="JetBrains Mono">
                <textPath href="#circ">BESPOKE · JOINERY · SINCE · ALWAYS ·</textPath>
              </text>
            </svg>
            <img src="/emblem-light.png" alt="" className="h-11 w-auto" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
